import { useCallback, useEffect, useState } from 'react';

type DeviceModelHint = {
  rawModel: string;
  supported: boolean;
  isLoading: boolean;
  error: Error | null;
  isForbiddenModel: boolean;
  deviceNotRedmiNote: boolean;
  refresh: () => Promise<string | null>;
};

// These are all models that we know to have a problem with refreshing SmartScan site after user returns from Native Camera after taking a photo
const forbiddenModelMap: Record<string, string> = {
  // Redmi Note 11 series (common codes seen in public specs / lists)
  '2201117TY': 'Redmi Note 11 (NFC / regional variant)',
  '2201117TG': 'Redmi Note 11 (Global variant)',
  '2201117TI': 'Redmi Note 11 (India / regional variant)',
  '2201117T': 'Redmi Note 11 (other regional variant)',
  '2201116TG': 'Redmi Note 11 Pro (Global)',
  '2201116TI': 'Redmi Note 11 Pro (India)',
  '2201116SG': 'Redmi Note 11 Pro 5G / POCO X4 Pro (variant)',
  '2201116SI': 'Redmi Note 11 Pro+ / India (variant)',
  '2201116SC': 'Redmi Note 11E Pro / variant',
  '22031116BG': 'Redmi Note 11S 5G (variant)',
  // Redmi Note 12 series (many regional variants)
  '23021RAAEG': 'Redmi Note 12 (global variant)',
  '23028RA60L': 'Redmi Note 12 (Latin America variant)',
  '23021RAA2Y': 'Redmi Note 12 (NFC / variant)',
  '22111317G': 'Redmi Note 12 5G (Global)',
  '22111317I': 'Redmi Note 12 5G (India)',
  '22101317C': 'Redmi Note 12 5G / related variant',
  '22101316G': 'Redmi Note 12 Pro 5G / Pro (Global)',
  '22101316C': 'Redmi Note 12 Pro 5G (variant)',
  '22101316I': 'Redmi Note 12 Pro 5G (India)',
  '22101316UG': 'Redmi Note 12 Pro+ / Pro+ variant',
  '22101316UCP': 'Redmi Note 12 Pro+ / variant',
  '22101316UC': 'Note 12 Discovery / variant',
  '22101320C': 'Note 12 Pro Speed / Poco X5 Pro variant',
  '23049RAD8C': 'Note 12 Turbo / Poco F5 (variant)',
  '23054RA19C': 'Note 12T Pro (variant)',
  // Redmi Note 13 series (common codes found in public device DBs / vendor pages)
  '23129RAA4G': 'Redmi Note 13 4G (global / standard)',
  '23129RAA6G': 'Redmi Note 13 (other region variant)',
  '23090RA98G': 'Redmi Note 13 Pro (global variant)',
  '23090RA98I': 'Redmi Note 13 Pro (India)',
  '23117RA68G': 'Redmi Note 13 Pro+ (global)',
  '23117RA68I': 'Redmi Note 13 Pro+ (India)',
  '23129RAA2G': 'Redmi Note 13 (other variant)'
};

/**
 * Retrieves the device model via User-Agent Client Hints (if supported).
 * Returns the raw model string plus helpers for loading/error state.
 */
export const useDeviceModelHint = (): DeviceModelHint => {
  const isSupported = typeof navigator !== 'undefined' && !!(navigator as any).userAgentData;
  const [rawModel, setRawModel] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [isForbiddenModel, setIsForbiddenModel] = useState<boolean>(false);

  const fetchModel = useCallback(async () => {
    if (!isSupported) return null;

    setIsLoading(true);
    setError(null);

    try {
      const uaData = (navigator as any).userAgentData;
      const { model } = await uaData.getHighEntropyValues(['model']);
      const normalizedModel = (model || '').trim();
      const forbidden = !!forbiddenModelMap[normalizedModel];

      setRawModel(normalizedModel);
      setIsForbiddenModel(forbidden);
      return normalizedModel;
    } catch (err) {
      setError(err as Error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported]);

  useEffect(() => {
    fetchModel();
  }, [fetchModel]);

  return {
    rawModel,
    supported: isSupported,
    isLoading,
    error,
    isForbiddenModel,
    deviceNotRedmiNote: !isForbiddenModel,
    refresh: fetchModel,
  };
};
